import React from 'react'
import { Button, Skeleton } from '@/shared/ui'
import { Table, Tbody, Td, Th, Thead, Tr } from '@/shared/ui/Table'
import type { FinanceCostCategory } from '../../services/financeCostsService'
import { deleteFinanceCostCategory } from '../../services/financeCostsService'
import { FinanceCostCategoryModal } from './FinanceCostCategoryModal'
import { useToast } from '@/shared/ui/Toast'

type Props = {
  categories: FinanceCostCategory[]
  loading: boolean
  onRefresh: () => Promise<void>
}

export function FinanceCostCategoriesPanel({ categories, loading, onRefresh }: Props) {
  const { add: toast } = useToast()
  const [open, setOpen] = React.useState(false)
  const [editing, setEditing] = React.useState<FinanceCostCategory | null>(null)
  const [deletingId, setDeletingId] = React.useState<string | null>(null)

  async function onDelete(id: string) {
    const cat = categories.find((c) => c.id === id)
    const ok = window.confirm(`Excluir a categoria "${cat?.name || '—'}"? (os custos vinculados ficarão sem categoria)`)
    if (!ok) return
    try {
      setDeletingId(id)
      await deleteFinanceCostCategory(id)
      toast({ title: 'Categoria excluída' })
      await onRefresh()
    } catch (e: any) {
      toast({ title: 'Falha ao excluir', description: e?.message || String(e), variant: 'error', soft: true })
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-foreground/70">Categorias de custos (usadas em recorrentes e avulsos).</div>
        <Button onClick={() => { setEditing(null); setOpen(true) }}>Nova categoria</Button>
      </div>

      <div className="rounded-2xl border border-slate-800/80 bg-slate-950/80 p-4">
        {loading ? (
          <Skeleton className="h-28 w-full" />
        ) : (
          <div className="overflow-auto">
            <Table>
              <Thead>
                <Tr>
                  <Th>Nome</Th>
                  <Th className="text-right">Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {categories.map((c) => (
                  <Tr key={c.id}>
                    <Td className="font-medium">{c.name}</Td>
                    <Td className="text-right whitespace-nowrap">
                      <div className="inline-flex gap-2">
                        <Button className="h-8 px-3" variant="outline" onClick={() => { setEditing(c); setOpen(true) }}>Editar</Button>
                        <Button className="h-8 px-3" variant="outline" disabled={deletingId === c.id} onClick={() => onDelete(c.id)}>
                          {deletingId === c.id ? 'Excluindo...' : 'Excluir'}
                        </Button>
                      </div>
                    </Td>
                  </Tr>
                ))}
                {!categories.length && (
                  <Tr>
                    <Td colSpan={2} className="text-sm text-foreground/70">Nenhuma categoria.</Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </div>
        )}
      </div>

      <FinanceCostCategoryModal
        open={open}
        onClose={() => setOpen(false)}
        onSaved={onRefresh}
        initial={editing}
      />
    </div>
  )
}
